import React, { useState } from 'react';
import { AwaitingItem, DestinationGroup } from '../types';

interface Props {
  groups: DestinationGroup[];
  selected: string[];
  onToggle: (destination: string) => void;
  maxWaitDays: number;
  /** Ključevi naloga izbačenih iz ture, oblika „odredište|nalog". */
  excluded: string[];
  onToggleDocument: (destination: string, document: string | null) => void;
}

/** Ključ pod kojim se nalog izbacuje. Isti oblik i ovde i u App-u. */
export function documentKey(destination: string, document: string | null): string {
  return `${destination}|${document ?? ''}`;
}

/** Nalozi jedne grupe, sa količinom i najdužim čekanjem. */
function byDocument(items: AwaitingItem[]) {
  const map = new Map<string, { document: string | null; items: AwaitingItem[] }>();

  for (const item of items) {
    const key = item.sourceDocument ?? '';
    const bucket = map.get(key);
    if (bucket) bucket.items.push(item);
    else map.set(key, { document: item.sourceDocument, items: [item] });
  }

  return Array.from(map.values())
    .map((bucket) => ({
      document: bucket.document,
      items: bucket.items,
      quantity: bucket.items.reduce((sum, i) => sum + i.quantity, 0),
      waitingDays: Math.max(...bucket.items.map((i) => i.waitingDays))
    }))
    .sort((a, b) => b.waitingDays - a.waitingDays);
}

/**
 * Roba koja čeka, grupisana po odredištu.
 *
 * Bira se više odredišta odjednom — ona postaju stanice iste ture. Jedno mesto
 * retko napuni vozilo; tura je mešovita i ide po pravcu.
 *
 * Odredište se može i <b>razložiti na naloge</b>, jer tovar ne stane uvek: tada
 * operater skida ceo nalog, a ne komad. Nalog se kod nas ne deli — ako treba
 * manje od celog, razbija se u ERP-u, kako MPO ne bi dobio nalog koji ne može da
 * primi u celosti.
 */
export default function AwaitingPanel({
  groups, selected, onToggle, maxWaitDays, excluded, onToggleDocument
}: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (groups.length === 0) {
    return <p className="empty">Nema robe koja čeka na transport.</p>;
  }

  return (
    <ul className="destinations">
      {groups.map((group) => {
        const position = selected.indexOf(group.destination);
        const isSelected = position >= 0;
        const documents = byDocument(group.items);
        const isExpanded = expanded === group.destination;

        const droppedQuantity = documents
          .filter((d) => excluded.includes(documentKey(group.destination, d.document)))
          .reduce((sum, d) => sum + d.quantity, 0);

        return (
          <li key={group.destination}>
            {/* Klik na karticu RAZLAŽE odredište, ne stavlja ga na turu.
                Ranije je jedan klik ubacivao ceo objekat sa svim nalozima —
                operater koji je hteo samo da pogleda šta tu ima, dobijao je
                novu turu. Na turu se ide izričitim dugmetom sa strane. */}
            <button
              type="button"
              className={`destination ${isSelected ? 'destination--selected' : ''} ${
                group.hasOverdue ? 'destination--overdue' : ''
              }`}
              aria-expanded={isExpanded}
              onClick={() => setExpanded(isExpanded ? null : group.destination)}
            >
              <span className="destination__name">
                {isSelected && <span className="destination__order">{position + 1}</span>}
                {group.destination}
              </span>
              <span className="destination__meta">
                {group.items.length} stavki · {group.totalQuantity} kom
                {/* Izbačeno se prikazuje na samoj grupi: inače se ne vidi da tura
                    nosi manje nego što odredište čeka. */}
                {droppedQuantity > 0 && (
                  <strong className="destination__dropped"> · {droppedQuantity} kom izbačeno</strong>
                )}
              </span>
              <span className="destination__waiting">
                {group.hasOverdue ? (
                  <strong>
                    {group.overdueCount} od {group.items.length} stavki preko {maxWaitDays} d
                  </strong>
                ) : (
                  <>sve unutar {maxWaitDays} d</>
                )}
              </span>
              <span className="destination__age">najstarije čeka {group.oldestWaitingDays} d</span>
              {group.needsReviewCount > 0 && (
                <span className="destination__review">
                  {group.needsReviewCount} bez potvrđenog odredišta
                </span>
              )}
            </button>

            <div className="destination__act">
              <button
                type="button"
                className={isSelected ? '' : 'primary'}
                onClick={() => onToggle(group.destination)}
                aria-pressed={isSelected}
              >
                {isSelected ? 'Skini sa ture' : 'Na turu'}
              </button>

              <span className="muted">
                {documents.length} {documents.length === 1 ? 'nalog' : 'naloga'}
                {isExpanded ? ' · razloženo' : ''}
              </span>
            </div>

            {isExpanded && (
              <div className="orders">
                {documents.map((entry) => {
                  const key = documentKey(group.destination, entry.document);
                  const isDropped = excluded.includes(key);

                  return (
                    <div key={key} className={`orders__row ${isDropped ? 'orders__row--dropped' : ''}`}>
                      <span className="orders__name">
                        {entry.document ? `Nalog ${entry.document}` : 'Bez naloga'}
                        <span className="muted">
                          {' '}· {entry.quantity} kom · čeka {entry.waitingDays} d
                        </span>
                      </span>

                      <span className="orders__items muted">
                        {entry.items.map((i) => `${i.articleName} × ${i.quantity}`).join(', ')}
                      </span>

                      <button type="button" onClick={() => onToggleDocument(group.destination, entry.document)}>
                        {isDropped ? 'Vrati na turu' : 'Izbaci sa ture'}
                      </button>
                    </div>
                  );
                })}

                <p className="note">
                  Skida se ceo nalog. Ako treba manje od njega, razbij nalog u ERP-u
                  na dva — inače objekat dobija nalog koji ne može da primi u
                  celosti.
                </p>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
